import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  PlusOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { assemblyEstimateService } from '@/services/assemblyEstimate.service';
import { openAssemblyEstimatePrint } from '@/utils/openAssemblyEstimatePrint';
import { formatMoney as displayAmount, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;

type EstimateRow = {
  id: string;
  estimate_number?: string;
  estimate_name?: string;
  customer_name?: string;
  project_name?: string;
  revision_number?: number;
  status?: string;
  estimated_cost?: number | string;
  selling_price?: number | string;
  currency?: string;
};

const STATUS_COLORS: Record<string, string> = {
  Draft: 'blue',
  'In Review': 'orange',
  Approved: 'green',
  Rejected: 'red',
  Converted: 'purple',
};

function money(v: unknown, currency?: string) {
  return displayAmount(Number(v || 0), {
    ...PRINT_PLAIN_AMOUNT_PROFILE,
    ...(currency ? { currency } : {}),
  });
}

const AssemblyEstimateListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const perms = user?.permissions || [];
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const canCreate = isSuperAdmin || perms.includes('growth.assembly_estimation.create');
  const canDelete = isSuperAdmin || perms.includes('growth.assembly_estimation.delete');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const query = useQuery({
    queryKey: ['assembly-estimates', search],
    queryFn: () => assemblyEstimateService.list(search),
  });

  const rows = useMemo(() => {
    const all = (query.data?.data || []) as EstimateRow[];
    if (!statusFilter) return all;
    return all.filter((r) => r.status === statusFilter);
  }, [query.data, statusFilter]);

  const totals = useMemo(() => {
    const estimated = rows.reduce((s, r) => s + Number(r.estimated_cost || 0), 0);
    const selling = rows.reduce((s, r) => s + Number(r.selling_price || 0), 0);
    return { count: rows.length, estimated, selling };
  }, [rows]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => assemblyEstimateService.remove(id),
    onSuccess: () => {
      message.success('Estimate deleted');
      queryClient.invalidateQueries({ queryKey: ['assembly-estimates'] });
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || 'Delete failed');
    },
  });

  const copyMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await assemblyEstimateService.copy(id);
      return (res as { data?: { estimate?: { id: string } } })?.data?.estimate?.id
        || (res as { estimate?: { id: string } })?.estimate?.id;
    },
    onSuccess: (id) => {
      message.success('Estimate copied');
      queryClient.invalidateQueries({ queryKey: ['assembly-estimates'] });
      if (id) navigate(`/growth/estimations/assembly/${id}`);
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || 'Copy failed');
    },
  });

  const confirmDelete = (row: EstimateRow) => {
    Modal.confirm({
      title: `Delete ${row.estimate_number}?`,
      content: 'Soft-deletes this revision. This cannot be undone from the list.',
      okType: 'danger',
      okText: 'Delete',
      onOk: () => deleteMutation.mutateAsync(row.id),
    });
  };

  const columns: ColumnsType<EstimateRow> = [
    {
      title: 'Number',
      dataIndex: 'estimate_number',
      render: (v, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/estimations/assembly/${row.id}`)}>
          {v}
        </Button>
      ),
    },
    { title: 'Name', dataIndex: 'estimate_name' },
    { title: 'Customer', dataIndex: 'customer_name' },
    { title: 'Project', dataIndex: 'project_name', ellipsis: true },
    { title: 'Rev', dataIndex: 'revision_number', width: 64 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status: string) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
    },
    {
      title: 'Estimated',
      dataIndex: 'estimated_cost',
      align: 'right',
      render: (v, row) => money(v, row.currency),
    },
    {
      title: 'Selling',
      dataIndex: 'selling_price',
      align: 'right',
      render: (v, row) => money(v, row.currency),
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      render: (_: unknown, row) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'open',
                icon: <EyeOutlined />,
                label: 'Open',
                onClick: () => navigate(`/growth/estimations/assembly/${row.id}`),
              },
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Edit workspace',
                onClick: () => navigate(`/growth/estimations/assembly/${row.id}`),
              },
              ...(canCreate
                ? [
                    {
                      key: 'copy',
                      icon: <CopyOutlined />,
                      label: 'Copy',
                      onClick: () => copyMutation.mutate(row.id),
                    },
                  ]
                : []),
              {
                key: 'print',
                icon: <PrinterOutlined />,
                label: 'Print',
                onClick: () => openAssemblyEstimatePrint(row.id),
              },
              {
                key: 'export',
                icon: <DownloadOutlined />,
                label: 'Export Excel',
                onClick: () =>
                  assemblyEstimateService
                    .downloadExport(row.id, row.estimate_number)
                    .then(() => message.success('Exported'))
                    .catch((error: any) => message.error(error?.response?.data?.message || error?.message || 'Export failed')),
              },
              ...(canDelete
                ? [
                    { type: 'divider' as const },
                    {
                      key: 'delete',
                      icon: <DeleteOutlined />,
                      danger: true,
                      label: 'Delete',
                      disabled: !['Draft', 'Rejected'].includes(String(row.status || '')),
                      onClick: () => confirmDelete(row),
                    },
                  ]
                : []),
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <Title level={2} style={{ margin: 0 }}>Assembly-Based Estimation</Title>
              <Text type="secondary">
                Build cost from manufacturing assemblies, approve, then convert to quotation through the BOQ chain.
              </Text>
            </div>
            <Space wrap>
              <Button
                icon={<DownloadOutlined />}
                onClick={() =>
                  assemblyEstimateService
                    .downloadImportTemplate()
                    .then(() => message.success('Template downloaded'))
                    .catch((error: any) => message.error(error?.response?.data?.message || error?.message || 'Download failed'))
                }
              >
                Import template
              </Button>
              <Button type="primary" icon={<PlusOutlined />} disabled={!canCreate} onClick={() => navigate('/growth/estimations/assembly/new')}>
                New estimate
              </Button>
            </Space>
          </div>

          <Row gutter={16}>
            <Col xs={24} sm={8}><Statistic title="Estimates" value={totals.count} /></Col>
            <Col xs={24} sm={8}><Statistic title="Estimated cost (list)" value={money(totals.estimated)} /></Col>
            <Col xs={24} sm={8}><Statistic title="Selling (list)" value={money(totals.selling)} /></Col>
          </Row>

          <Space wrap style={{ width: '100%' }}>
            <Input.Search
              placeholder="Search number, name, or customer"
              allowClear
              onSearch={setSearch}
              style={{ width: 320 }}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={['Draft', 'In Review', 'Approved', 'Rejected', 'Converted'].map((s) => ({ value: s, label: s }))}
            />
          </Space>

          {query.isError ? (
            <Alert type="error" showIcon message="Could not load assembly estimates" />
          ) : null}

          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={rows}
            columns={columns}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            onRow={(row) => ({
              onClick: () => navigate(`/growth/estimations/assembly/${row.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        </Space>
      </Card>
    </div>
  );
};

export default AssemblyEstimateListPage;
